import { useEffect, useState, type ReactNode } from "react";

import { track } from "../analytics";
import { HOME } from "../content/home";
import { type Lang, langFromPath } from "../i18n/lang";
import { Page } from "../layout/Page";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { Reveal } from "../ui/Reveal";
import { Section } from "../ui/Section";

import { UpcomingBlock, WeeklySessions } from "./events/UpcomingBlock";

/** Wraps every occurrence of each phrase in `<strong>`, keeping the rest of `body` verbatim. */
function emphasise(body: string, phrases: readonly string[]): ReactNode[] {
  const pattern = new RegExp(
    `(${phrases.map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})`,
    "g",
  );
  return body
    .split(pattern)
    .filter((part) => part.length > 0)
    .map((part, i) =>
      phrases.includes(part) ? (
        <strong key={i}>{part}</strong>
      ) : (
        <span key={i}>{part}</span>
      ),
    );
}

function Testimonials({ lang }: { lang: Lang }) {
  const [i, setI] = useState(0);
  const t = HOME.testimonials[i] ?? HOME.testimonials[0];
  if (!t) {
    return null;
  }
  return (
    <figure className="max-w-2xl rounded-2xl border border-hairline bg-card p-6 shadow-soft md:p-8">
      <blockquote className="min-h-56 text-base leading-relaxed text-ink md:text-lg">
        {t.text[lang]}
      </blockquote>
      <figcaption className="mt-4 text-sm font-semibold text-teal-deep">
        {t.name}, {t.place}
      </figcaption>
      <div
        className="mt-1 -mb-4 -ml-4 flex items-center"
        role="group"
        aria-label="Testimonials"
      >
        {HOME.testimonials.map((x, idx) => (
          <button
            key={x.name}
            type="button"
            onClick={() => setI(idx)}
            aria-label={`Show testimonial ${idx + 1}`}
            aria-pressed={idx === i}
            className="flex min-h-11 min-w-11 items-center justify-center rounded-full transition-colors hover:bg-ivory-deep focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-teal"
          >
            <span
              aria-hidden="true"
              className={`h-3 w-3 rounded-full transition-colors ${idx === i ? "bg-magenta" : "bg-hairline-strong"}`}
            />
          </button>
        ))}
      </div>
    </figure>
  );
}

export function Home() {
  useEffect(() => {
    track("home_viewed");
  }, []);
  const { lang } = langFromPath(window.location.pathname);
  const about = HOME.about;
  return (
    <Page>
      <section className="page-heading bg-ivory pb-8 pt-16 md:pb-14 md:pt-28">
        <div className="mx-auto max-w-6xl px-5 md:px-10">
          <p className="eyebrow">1 to 4 October 2026 · Singapore</p>
          <h1 className="font-display mt-5 max-w-3xl text-[2.6rem] leading-[1.02] text-ink md:text-[4.4rem]">
            Swamiji is coming to Singapore
          </h1>
          <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-soft">
            Sadguru Swatantra Deo Ji Maharaj and Sant Shri Naam Deo Ji Maharaj
            visit this October. On this occasion, four programmes are organised:
            two by invitation, two open to everyone.
          </p>
          <div className="mt-9 flex flex-wrap gap-3">
            <Button href="/register" size="lg">
              Register
            </Button>
            <Button href="/#upcoming" variant="secondary" size="lg">
              See the programmes
            </Button>
          </div>
        </div>
      </section>

      <Section id="upcoming" eyebrow="The programmes">
        <UpcomingBlock />
      </Section>

      <Section
        id="meditation"
        title={HOME.sections.meditation[lang]}
        tone="ivory-deep"
      >
        <div className="grid gap-6 md:grid-cols-3">
          {HOME.meditation.map((c) => (
            <Reveal key={c.title.en}>
              <Card
                eyebrow={c.eyebrow[lang]}
                title={c.title[lang]}
                image={{ src: c.image, alt: "" }}
                cta={{ ...c.cta, label: c.cta.label[lang] }}
              >
                {c.lines.map((l) => (
                  <p key={l.en}>{l[lang]}</p>
                ))}
              </Card>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section id="weekly" title="Weekly sessions">
        <WeeklySessions />
      </Section>

      <Section id="quotes" title={HOME.sections.quotes[lang]}>
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {HOME.quotes.map((q) => (
            <Reveal key={q.en}>
              <p className="font-display text-[1.05rem] leading-relaxed text-ink-soft">
                {q[lang]}
              </p>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section
        id="testimonials"
        title={HOME.sections.testimonials[lang]}
        tone="ivory-deep"
      >
        <Testimonials lang={lang} />
      </Section>

      <Section id="about" title={about.title[lang]}>
        <div className="grid gap-8 md:grid-cols-[2fr_1fr]">
          <div>
            <p className="text-base leading-relaxed text-ink md:text-lg">
              {emphasise(about.body[lang], about.emphasis[lang])}
            </p>
            <div className="mt-6">
              <Button href={about.cta.href} variant="secondary">
                {about.cta.label[lang]}
              </Button>
            </div>
          </div>
          <aside>
            <h3 className="font-display text-xl text-stone">
              {HOME.sections.moreAbout[lang]}
            </h3>
            <ul className="mt-3 space-y-2">
              {HOME.moreAbout.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-teal-deep underline-offset-4 hover:underline"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </aside>
        </div>
      </Section>

      <Section id="posters" title="From our gatherings" tone="ivory-deep">
        <ul className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {HOME.posters.map((p) => (
            <li key={p.src}>
              <img
                src={p.src}
                alt={p.alt}
                className="aspect-[9/5] w-full rounded-md object-cover"
                loading="lazy"
              />
            </li>
          ))}
        </ul>
      </Section>
    </Page>
  );
}
